import { useEffect, useState } from "react";
import { getCredentials } from "@connect/credentials-storage";
import { createFramesClient } from "@connect/data/frames/client";
import { log } from "@connect/observability";
import { apiTargetHeader } from "../../lib/parse-frame-config";
import type { StructuredAddress } from "@connect/component-library/components/address-autocomplete";
import { mapCustomerAddress } from "./map-customer-address";

interface UseCustomerAddressArgs {
  baseUrl: string;
  clientToken: string;
  apiTarget?: string;
  /** Country used when the saved address lacks one; keeps a mapped address from shipping alpha3 "". */
  fallbackCountry: string;
}

// Owns the add-card billing address, seeded from the customer's saved profile (DEVXP-1054).
// The prefill stays editable and never clobbers an address the user already picked; it
// silently no-ops when none is on file, the request fails, or a value was set first.
// Partner platform routes take the partner client token in `x-client-token` and the customer
// token as Bearer, so the client is built with explicit headers rather than the customer-token
// authMiddleware (mirrors customer-export). Returns a `useState`-shaped tuple.
export function useCustomerAddress({
  baseUrl,
  clientToken,
  apiTarget,
  fallbackCountry,
}: UseCustomerAddressArgs) {
  const [address, setAddress] = useState<StructuredAddress | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { customerToken } = await getCredentials();
      if (!customerToken) {
        return;
      }
      const framesClient = createFramesClient({
        baseUrl,
        headers: {
          ...apiTargetHeader({ apiTarget }),
          "x-client-token": clientToken,
          Authorization: `Bearer ${customerToken}`,
        },
      });
      const res = await framesClient.GET("/platform/v1/partner/customer/current").catch(() => null);
      if (cancelled) {
        return;
      }
      if (!res || res.error) {
        // 404 = no profile on file (expected); anything else is unexpected.
        if (!res || res.response.status !== 404) {
          log.warn("Customer address prefill request failed", {
            status: res?.response.status ?? "network_error",
          });
        }
        return;
      }
      if (!res.data.customer.address) {
        return;
      }
      const mapped = mapCustomerAddress(res.data.customer.address, fallbackCountry);
      if (mapped) {
        setAddress((curr) => curr ?? mapped);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, apiTarget, fallbackCountry, clientToken]);

  return [address, setAddress] as const;
}
